import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Text,
  Flex,
  Image,
  Button,
  ModalFooter,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { softwareLanguages } from "./constants/data";

import BarStrip from "./BarStrip";
import LanguageSelector from "./LanguageSelector";
import { EmailInputStage } from "./EmailInputStage";
import { DetailsStage } from "./DetailsStage";
import { VerificationLinkStage } from "./VerificationLinkStage";
import useSignup from "./useSignup";
import useEmbedMode, { useEmbedMaxWidth } from "./hooks/useEmbedMode";
import useEmbedAutoHeight, {
  isEmbedAutoHeightEnabled,
} from "./hooks/useEmbedAutoHeight";
import useThemeMode, {
  useThemeTokens,
  useSquareCorners,
} from "./hooks/useThemeMode";
import { safeSet } from "./utils/safeStorage";

export const openLink = (url) => {
  window.open(url, "_blank");
};

const MailingListModel = ({
  isOpen,
  onClose,
  apiUrl,
  initialStage,
  initialEmail,
  initialAuthToken,
  initialLang,
}) => {
  const { t, i18n } = useTranslation();
  const embed = useEmbedMode();
  const embedMaxW = useEmbedMaxWidth();
  // Report the content height to the host, so it can size the iframe to the
  // form. A no-op outside embed mode or when ?resize=0 turns it off.
  useEmbedAutoHeight();
  const autoHeight = isEmbedAutoHeightEnabled();
  // ?theme=light renders the card white. Dark stays the default.
  const light = useThemeMode();
  const tokens = useThemeTokens();
  // ?button_style=square squares the card and buttons. Default is rounded.
  const square = useSquareCorners();
  const [email, setEmail] = useState(initialEmail || "");
  const [stage, setStage] = useState(initialStage || "email-input");
  const [authToken, setAuthToken] = useState(initialAuthToken || "");
  const { signup, tagExistingContact } = useSignup(apiUrl);

  // New Mailing List User Flow
  // Stage 1: Email Input
  // Stage 2: (Register Newsletter): Personal Info
  // Stage 3: (Register Newsletter): Registration Complete

  // Software User Flow
  // Stage 1: Email Input
  // Stage 2: (Register Software): Personal Info
  // Stage 3: (Register Software): Registration Complete

  const handleEmailSubmit = useCallback(
    async (submittedEmail, exists, isMarketingSignup) => {
      setEmail(submittedEmail);
      safeSet("isMarketingSignup", "false");

      // Software signup
      if (exists && !isMarketingSignup) {
        const response = await signup(
          {
            email: submittedEmail,
            firstName: "-",
            lastName: "-",
            country: "US",
            language: "en",
            marketSegment: "consumer",
            marketWorkAreaSegment: "content_creation",
            contactConsent: true,
          },
          false,
          true
        );
        if (response && (response.user || response.requiresVerification)) {
          if (response.alreadyRegistered) {
            setStage("already-registered");
          } else {
            setStage("registration-complete");
          }
        }
      } else {
        // An existing newsletter contact fills the details form next, and may leave it. Tag
        // the contact now, so the funnel is recorded whether or not the form is finished.
        if (exists) {
          await tagExistingContact(submittedEmail);
        }
        setStage("details");
      }
    },
    [signup, tagExistingContact]
  );

  const handleDetailsSubmit = useCallback(({ alreadyRegistered }) => {
    console.log("Registration data submitted", { email, alreadyRegistered });
    if (alreadyRegistered) {
      setStage("already-registered");
    } else {
      setStage("registration-complete");
    }
  }, [email]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // ?lang= is handled by the i18next querystring detector (see i18n.js), so
    // it applies on first paint rather than after a render in English. Only the
    // prop form is left here — it is how the desktop software and the Storybook
    // stories set the language, with no URL to read.
    if (initialLang) {
      const isSupported = softwareLanguages.some(
        (lang) => lang.short === initialLang
      );
      if (isSupported) {
        i18n.changeLanguage(initialLang);
      } else {
        console.warn(`Language not supported: ${initialLang}. Using default.`);
      }
    }

    // Handle token from props or URL
    const token = initialAuthToken || urlParams.get("token");
    if (token) {
      setAuthToken(token);

      if (window.location.href.includes("reset-password")) {
        setStage("reset-password");
      } else if (window.location.href.includes("verify")) {
        setStage("verification-link");
      }
    }
  }, [i18n, initialLang, initialAuthToken]);

  return (
    // In embed mode the Modal wrapper is kept but every modal *behaviour* is
    // switched off. The wrapper has to stay: ModalFooter (used here and in
    // EmailInputStage and DetailsStage) calls Chakra's useStyles(), which
    // throws outside a ModalContent. Keeping it means the stage components
    // need no changes at all.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      isCentered={!embed}
      // "lg" (32rem) is the standalone width and stays that. Embed mode widens
      // to 640 via maxW below — it used to use "full", which meant no cap at
      // all, and in a full-width iframe the card stretched to the viewport.
      size="lg"
      // The host page's own scrolling must keep working — this lock on the
      // iframe document is what made the Calibrite page feel stuck.
      blockScrollOnMount={!embed}
      // A focus trap in a frame the visitor cannot dismiss is a keyboard
      // dead-end; tab has to be able to leave the iframe.
      trapFocus={!embed}
      // EmailInputStage autofocuses its email field. Focusing an input inside
      // an iframe scrolls the host page to it on load.
      autoFocus={!embed}
      motionPreset={embed ? "none" : undefined}
    >
      {!embed && <ModalOverlay />}
      <ModalContent
        // Default is position:fixed; inset:0, which pins the card to the iframe
        // viewport. Static lets it sit in normal flow and size to its content.
        containerProps={embed ? { position: "static" } : undefined}
        backgroundColor={tokens.card}
        color={tokens.text}
        // Light: a flat #cccccc border, no outline, no shadow. Dark: the shipped
        // white outline (dropped in embed). Corners follow ?button_style (square)
        // and embed; colours follow ?theme.
        border={light ? tokens.cardBorder : undefined}
        outline={light || embed ? "none" : "0.063rem solid white"}
        boxShadow={light || embed ? "none" : undefined}
        borderRadius={square || embed ? 0 : undefined}
        my={embed ? 0 : undefined}
        // Fill a narrow frame, cap at 640 (or ?maxw=) and centre in anything
        // wider — a full-bleed form in a wide container reads as broken.
        w={embed ? "100%" : undefined}
        maxW={embed ? embedMaxW : undefined}
        mx={embed ? "auto" : undefined}
      >
        {/* The logo, the colour bar and the language switcher are the branding of
            our own page. The host page supplies its own branding around the
            iframe, so embed mode shows the heading and the form only. The host
            site also owns the language and passes it as ?lang=, which would make
            the switcher a second, conflicting control. */}
        <ModalHeader>
          {!embed && (
            <>
              <Flex justifyContent={"space-between"}>
                <Image width={"40px"} mb={2} src={tokens.logo} />
                <LanguageSelector languages={softwareLanguages} />
              </Flex>
              <BarStrip />
            </>
          )}

          {/* mt separates the heading from the colour bar above it. In embed mode
              there is no colour bar, so the margin would be dead space. */}
          {stage === "verification-link" && (
            <Text mt={embed ? 0 : 4} fontSize="2xl" fontWeight="bold">
              {t("heading.Verify Your Account")}
            </Text>
          )}

          {stage !== "verification-link" && (
            <Text mt={embed ? 0 : 4} fontSize="2xl" fontWeight="bold">
              {t("heading.Sign up to our mailing list")}
            </Text>
          )}
        </ModalHeader>
        {/* When auto-height is on, the iframe grows to the form, so the card
            must not add its own scrollbar — one scroll owner only. The inner
            "auto" scroll stays as a safety net for a fixed-height host that does
            not run the resize script (embed with ?resize=0). */}
        <ModalBody
          overflowY={embed ? (autoHeight ? "visible" : "auto") : undefined}
        >
          {stage === "email-input" && (
            <EmailInputStage
              onNext={handleEmailSubmit}
              apiUrl={apiUrl}
              newsletter={true}
            />
          )}
          {stage === "details" && (
            <DetailsStage
              onSubmit={handleDetailsSubmit}
              email={email}
              onBack={() => setStage("email-input")}
              apiUrl={apiUrl}
              newsletter={true}
              openSoftwareLicense={() => {}}
            />
          )}
          {stage === "registration-complete" && (
            <>
              {" "}
              <Flex direction="column" gap={4}>
                <Text>
                  {t(
                    "label.Thank you check e-mail account to validate your sign-up"
                  )}

                  <Text>{t("label.e-mail can take up to 5 minutes.")}</Text>
                </Text>
              </Flex>
              {/* window.close() cannot work in a framed document, so in embed
                  mode the button would just sit there doing nothing. The
                  thank-you text is the terminal state instead. */}
              {!embed && (
                <ModalFooter>
                  <Button
                    colorScheme="green"
                    onClick={() => {
                      window.close();
                    }}
                    mr={3}
                  >
                    {t("button.Done")}
                  </Button>
                </ModalFooter>
              )}
            </>
          )}
          {stage === "already-registered" && (
            <>
              <Flex direction="column" gap={4}>
                <Text>
                  {t(
                    "label.mailing-already-subscribed",
                    "Thank you for signing up to our newsletter."
                  )}
                </Text>
              </Flex>
              {!embed && (
                <ModalFooter>
                  <Button
                    colorScheme="green"
                    onClick={() => {
                      window.close();
                    }}
                    mr={3}
                  >
                    {t("button.Done")}
                  </Button>
                </ModalFooter>
              )}
            </>
          )}
          {stage === "verification-link" && (
            <VerificationLinkStage token={authToken} apiUrl={apiUrl} />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default MailingListModel;
